import React, { useState } from 'react';
import './GameBoard.css';

const Player = {
  NONE: 0,
  RED: 1,
  YELLOW: 2
};

const GameState = {
  PLAYING: 'playing',
  PAUSED: 'paused',
  GAME_OVER: 'gameOver'
};

const GameBoard = ({ board, onColumnClick, currentPlayer, currentCol, gameState, isThinking, gameMode }) => {
  const [hoveredColumn, setHoveredColumn] = useState(-1);

  const isPlayable = gameState === GameState.PLAYING && !isThinking && 
                     !(gameMode === 'pvc' && currentPlayer === Player.YELLOW);

  const handleColumnHover = (col) => {
    if (isPlayable) {
      setHoveredColumn(col);
    }
  };

  const handleColumnLeave = () => {
    setHoveredColumn(-1);
  };

  const handleColumnClick = (col) => {
    if (isPlayable) {
      onColumnClick(col);
      setHoveredColumn(-1);
    }
  };

  const getColumnTopPosition = (col) => {
    // Find the lowest empty row in the column
    for (let row = board.length - 1; row >= 0; row--) {
      if (board[row][col] === Player.NONE) {
        return row;
      }
    }
    return -1; // Column is full
  };

  const renderCurrentBall = () => {
    if (gameState !== GameState.PLAYING && gameState !== GameState.TRANSITION) return null;

    return (
      <div 
        className={`current-ball ${currentPlayer === Player.RED ? 'red' : 'yellow'}`}
        style={{
          '--current-col': currentCol
        }}
      />
    );
  };

  const renderPreviewPiece = (col) => {
    const topRow = getColumnTopPosition(col);
    if (topRow === -1 || !isPlayable) return null;

    return (
      <div 
        className={`preview-piece ${currentPlayer === Player.RED ? 'red' : 'yellow'}`}
        style={{
          gridRow: topRow + 1,
          gridColumn: col + 1,
        }}
      />
    );
  };

  const renderBoardCell = (row, col) => {
    const cellValue = board[row][col];
    
    return (
      <div 
        key={`${row}-${col}`}
        className={`board-cell ${isPlayable ? 'clickable' : ''}`}
        style={{
          gridRow: row + 1,
          gridColumn: col + 1,
        }}
        onClick={() => handleColumnClick(col)}
        onMouseEnter={() => handleColumnHover(col)}
        onMouseLeave={handleColumnLeave}
      >
        <div className="cell-hole">
          {cellValue !== Player.NONE && (
            <div 
              className={`game-piece ${cellValue === Player.RED ? 'red' : 'yellow'}`}
            />
          )}
        </div>
      </div>
    );
  };

  const renderColumnHeaders = () => {
    return Array.from({ length: 7 }, (_, col) => {
      const topRow = getColumnTopPosition(col);
      const isColumnFull = topRow === -1;
      
      return (
        <div 
          key={`header-${col}`}
          className={`column-header ${isPlayable && !isColumnFull ? 'clickable' : ''} ${hoveredColumn === col ? 'hovered' : ''}`}
          style={{ gridColumn: col + 1 }}
          onClick={() => handleColumnClick(col)}
          onMouseEnter={() => handleColumnHover(col)}
          onMouseLeave={handleColumnLeave}
        >
          {hoveredColumn === col && !isColumnFull && isPlayable && (
            <div className={`drop-indicator ${currentPlayer === Player.RED ? 'red' : 'yellow'}`}>
              ↓
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="game-board-container">
      {/* Current ball floating above the board */}
      {renderCurrentBall()}
      
      <div className="board-grid">
        {/* Column headers for hover effects */}
        <div className="column-headers">
          {renderColumnHeaders()}
        </div>
        
        {/* Main game board */}
        <div className="board">
          {/* Render all board cells */}
          {board.map((row, rowIndex) =>
            row.map((_, colIndex) => renderBoardCell(rowIndex, colIndex))
          )}
          
          {/* Preview piece for hover effect */}
          {hoveredColumn !== -1 && renderPreviewPiece(hoveredColumn)}
        </div>
        
        {/* Thinking indicator for AI */}
        {isThinking && (
          <div className="thinking-overlay">
            <div className="thinking-indicator">
              <div className="spinner"></div>
              <span>AI is thinking...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameBoard;
